<template>
  <StoryblokComponent
    v-if="pageStory"
    :blok="pageStory.content"
    :breadcrumbs="breadcrumbs"
  />
</template>

<script setup lang="ts">
  const route = useRoute()
  const pageStore = usePageStore()
  const storyblokStore = useStoryblokStore()
  const { locale } = useI18n()
  const runtimeConfig = useRuntimeConfig()
  import type { IStoryblokPageData } from '@/stores/types'
  import type { ITranslatedSlug } from '@/components/types'
  import { createCampaignStoryblokError } from '@/utils/storyblok/campaignError'

  const CAMPAIGN_SLUG_PATTERN = /^[a-z0-9-]{1,120}$/
  const pageStory = ref<IStoryblokPageData | undefined>()

  function updateArrayStack(current: any, stack: any[]) {
    for (const item of current) {
      if (item && typeof item === 'object') stack.push(item)
    }

    return stack
  }

  function updateObjectStack(current: any, stack: any[]) {
    const keysToRemove = new Set(['file', 'fare_sheet', '_editable', 'id', 'promoItems'])
    for (const key in current) {
      if (keysToRemove.has(key)) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete current[key]
      } else if (current[key] && typeof current[key] === 'object') {
        stack.push(current[key])
      }
    }

    return stack
  }
  function removeUnwantedFields(obj: any) {
    const seen = new WeakSet()
    const stack = [obj]
    while (stack.length) {
      const current = stack.pop()
      if (!current || typeof current !== 'object' || seen.has(current)) continue
      seen.add(current)

      if (Array.isArray(current)) {
        updateArrayStack(current, stack)
      } else {
        updateObjectStack(current, stack)
      }
    }
  }

  const getStoryBlokData = async (slug: string) => {
    const { $i18n } = useNuxtApp()

    const language = $i18n.locale.value === 'en' ? 'default' : $i18n.locale.value

    const { data, error } = await useFetch('/api/storyblok/campaign/clp', {
      query: {
        slug: 'campaign/' + slug,
        lang: language
      }
    })

    if (error.value) {
      throw createCampaignStoryblokError(error.value)
    }

    return data
  }

  const slug = route.params.slug

  if (typeof slug !== 'string' || !CAMPAIGN_SLUG_PATTERN.test(slug)) {
    throw createError({
      statusCode: 404,
      statusMessage: 'global.error.pageNotFound',
      fatal: true
    })
  }

  try {
    const response = await getStoryBlokData(slug)

    if (!response?.value?.full_slug?.includes(slug)) {
      useRobotsRule(false)
      throw createError({
        statusCode: 404,
        statusMessage: 'global.error.pageNotFound',
        fatal: true
      })
    }

    useRobotsRule(true)

    if (slug.includes('campaign')) {
      removeUnwantedFields(response.value)
    }

    pageStory.value = response.value

    if (response.value) {
      storyblokStore.data = response.value
    }

    const defaultSlug = {
      lang: 'en',
      name: response.value?.name || '',
      path: response.value?.default_full_slug || '/',
      published: true
    }

    pageStore.setSlugs(response.value?.translated_slugs || [], [defaultSlug])
  } catch (e) {
    pageStory.value = undefined
    useRobotsRule(false)
    throw createError({
      statusCode: 404,
      statusMessage: 'global.error.pageNotFound',
      fatal: true,
      cause: e
    })
  }

  if (!pageStory.value) {
    throw createError({
      statusCode: 404,
      statusMessage: 'global.error.pageNotFound'
    })
  }

  if (pageStory.value.status) {
    throw createError({
      statusCode: pageStory.value.status,
      statusMessage: pageStory.value.response
    })
  }

  const seoContent = pageStory.value.content?.seo

  if (seoContent) {
    useSeoMeta({
      title: seoContent?.title,
      description: seoContent?.description,
      ogSiteName: 'airBaltic.com'
    })
  }

  const translatedSlugs = pageStory.value.translated_slugs
  const parsedTranslatedSlug = computed(() => parseTranslatedSlugs(translatedSlugs, locale.value))
  const breadcrumbs = computed(() => parseBreadcrumbs(pageStory, parsedTranslatedSlug.value.name, locale.value))

  useHead(() => {
    if (!pageStory.value) {
      return {}
    }

    const links = [
      {
        rel: 'canonical',
        href: runtimeConfig.public.siteUrl + trimLastSlash(route.path)
      },
      {
        rel: 'alternate',
        hreflang: 'x-default',
        href: runtimeConfig.public.siteUrl + '/en/' + trimLastSlash(pageStory.value?.default_full_slug)
      }
    ]

    translatedSlugs.forEach((slug: ITranslatedSlug) => {
      if (!slug?.name) return

      links.push({
        rel: 'alternate',
        hreflang: slug.lang,
        href: runtimeConfig.public.siteUrl + '/' + slug.lang + '/' + trimLastSlash(slug.path)
      })
    })

    const dayjs = useDayjs()

    const lastModified = pageStory.value?.updated_at ? dayjs(pageStory.value.updated_at).format('YYYY-MM-DD') : ''

    return {
      meta: [
        {
          name: 'language',
          content: locale.value
        },
        {
          name: 'lastModified',
          content: lastModified
        }
      ],
      link: links,
      titleTemplate: '%s %separator'
    }
  })
</script>
